"use client"

import type {
  HTMLStyledProps,
  ThemeProps,
  WithoutThemeProps,
} from "@/components/ui"
import type { InputProps } from "@/components/ui/components/input"
import type { ReactNode } from "react"
import type { PasswordInputStyle } from "./password-input.style"
import type { UsePasswordInputProps } from "./use-password-input"
import { useGroupItemProps } from "@yamada-ui/react/components/group"
import { EyeIcon, EyeOffIcon } from "@yamada-ui/react/components/icon"
import { createSlotComponent } from "@/components/ui"
import {
  Input,
  InputGroup,
  useInputPropsContext,
} from "@/components/ui/components/input"
import { passwordInputStyle } from "./password-input.style"
import { usePasswordInput } from "./use-password-input"

export interface PasswordInputProps
  extends
    WithoutThemeProps<InputProps, PasswordInputStyle>,
    ThemeProps<PasswordInputStyle>,
    UsePasswordInputProps {
  /**
   * The icons to be used for the visibility toggle.
   */
  visibilityIcon?: { off: ReactNode; on: ReactNode }
  /**
   * The props for the button element.
   */
  buttonProps?: PasswordInputButtonProps
  /**
   * The props for the end element.
   */
  elementProps?: InputGroup.ElementProps
  /**
   * The props for the root element.
   */
  rootProps?: InputGroup.RootProps
}

const {
  PropsContext: PasswordInputPropsContext,
  usePropsContext: usePasswordInputPropsContext,
  withContext,
  withProvider,
} = createSlotComponent<PasswordInputProps, PasswordInputStyle>(
  "password-input",
  passwordInputStyle,
)

export { PasswordInputPropsContext, usePasswordInputPropsContext }

/**
 * `PasswordInput` is a component that allows users to input passwords with a visibility toggle.
 *
 * @see https://yamada-ui.com/docs/components/password-input
 */
export const PasswordInput = withProvider<"input", PasswordInputProps>(
  ({
    className,
    css,
    colorScheme,
    visibilityIcon = { off: <EyeOffIcon />, on: <EyeIcon /> },
    buttonProps,
    elementProps,
    rootProps,
    ...props
  }) => {
    const [groupItemProps, rest] = useGroupItemProps(props)
    // @ts-ignore
    const { visible, getButtonProps, getInputProps } = usePasswordInput(rest)

    return (
      <InputGroup.Root
        className={className}
        css={css}
        colorScheme={colorScheme}
        {...groupItemProps}
        {...rootProps}
      >
        <PasswordInputField {...getInputProps()} />

        <InputGroup.Element clickable {...elementProps}>
          <PasswordInputButton {...getButtonProps(buttonProps)}>
            {visible ? visibilityIcon.off : visibilityIcon.on}
          </PasswordInputButton>
        </InputGroup.Element>
      </InputGroup.Root>
    )
  },
  "root",
)((props) => {
  const context = useInputPropsContext()

  return { ...context, ...props }
})

interface PasswordInputFieldProps extends InputProps {}

const PasswordInputField = withContext<"input", PasswordInputFieldProps>(
  Input,
  "field",
)({ "data-group-propagate": "" })

interface PasswordInputButtonProps extends HTMLStyledProps<"button"> {}

const PasswordInputButton = withContext<"button", PasswordInputButtonProps>(
  "button",
  "button",
)()
